// A drawn player: the ET character of PlayerAssets put together like
// CG_Player does it - the skeletal body (legs and torso frames, torso rotated
// by the view), the head on tag_head turned by the head angles, accessories on
// body and head tags, the knife on tag_weapon - skinned on the CPU every frame
// and lit like ET entities: light grid sample at the lighting origin, then
// RB_CalcDiffuseColor's 16 step table, written to the vertex colors the
// shaders' rgbGen lightingDiffuse reads.
//
// PlayerRenderKit holds what one map's players share: their materials (made
// with the map's shared shader uniforms) and the map's light grid.

import * as THREE from 'three';
import { EntityRenderKit, MeshPart, Part, SkinningState, surfaceShader } from '../render/entityParts';
import { PLAYER_WEAPON_NAME } from '../render/models/character';
import type { MdmSurface, MdmTag } from '../render/models/mdm';
import { Skeleton, findMdmTag, placeOnTag, type Orientation } from '../render/models/skeleton';
import type { SharedUniforms } from '../render/q3shader';
import type { TextureManager } from '../render/textures';
import type { Bsp } from '../shared/bsp';
import { PlayerAnimator, type AnimFrameInput } from './anim/playerAnim';
import type { PlayerAssets } from './playerAssets';

export type { EntityLight } from '../render/entityParts';

/** The kit of the players of a map: the entity kit with the character's shaders. */
export class PlayerRenderKit extends EntityRenderKit {
  constructor(
    readonly assets: PlayerAssets,
    textures: TextureManager,
    shared: SharedUniforms,
    bsp: Pick<Bsp, 'models' | 'lightGrid' | 'entityString'> | null,
  ) {
    super(assets.shaders, textures, shared, bsp);
  }
}

/** What the model needs to know to be drawn this frame. */
export interface PlayerDrawState extends AnimFrameInput {
  /** Camera position (distant players are lit up). */
  viewOrigin: ArrayLike<number>;
}

export class PlayerModel {
  readonly object = new THREE.Group();
  readonly animator: PlayerAnimator;
  private readonly skeleton: Skeleton;
  private readonly body: Part;
  private readonly bodySurfaces: MdmSurface[];
  private readonly head: MeshPart | null;
  private readonly accessories: { part: MeshPart; tag: string; onHead: boolean; mdmTag: MdmTag | null }[] = [];
  private readonly weapon: MeshPart | null;
  private readonly tags: Map<string, MdmTag | null> = new Map();
  private readonly assets: PlayerAssets;
  /** GPU skinning of the body (null: skinned on the CPU). */
  private readonly skinning: SkinningState | null;

  constructor(
    private readonly kit: PlayerRenderKit,
    clientNum: number,
    readonly ghost = false,
  ) {
    const a = (this.assets = kit.assets);
    this.animator = new PlayerAnimator(a.animModel, clientNum, PLAYER_WEAPON_NAME);
    this.skeleton = new Skeleton(a.numBones);
    this.body = new Part(this.object);
    this.bodySurfaces = a.mdm.surfaces;
    // the body is skinned on the GPU (the translucent ghost material keeps the CPU path)
    this.skinning = kit.gpuSkinning && !ghost ? new SkinningState(a.numBones) : null;
    for (const s of a.mdm.surfaces) {
      const shader = surfaceShader(a.bodySkin, s.name, s.shader);
      if (this.skinning) this.body.addSkinnedSurface(kit, shader, s, this.skinning, 80);
      else this.body.addSurface(kit, shader, s.numVerts, s.indexes, s.st, ghost, 80);
    }
    this.head = a.head ? new MeshPart(this.object, kit, a.head, a.headSkin, ghost) : null;
    for (const acc of a.accessories) {
      if (acc.onHead && !this.head) continue;
      this.accessories.push({ part: new MeshPart(this.object, kit, acc.model, acc.skin, ghost), tag: acc.tag, onHead: acc.onHead, mdmTag: acc.onHead ? null : findMdmTag(a.mdm, acc.tag) });
    }
    this.weapon = a.weapon ? new MeshPart(this.object, kit, a.weapon.model, a.weapon.skin, ghost) : null;
  }

  private bodyTag(name: string): Orientation | null {
    let tag = this.tags.get(name);
    if (tag === undefined) {
      tag = findMdmTag(this.assets.mdm, name);
      this.tags.set(name, tag);
    }
    return tag ? this.skeleton.tag(tag) : null;
  }

  /** Animates, skins, places and lights the model for one frame. */
  render(s: PlayerDrawState): void {
    const pose = this.animator.frame(s);
    const mdx = this.assets.mdx;
    const frameModel = mdx.get(pose.frameModel);
    const torsoModel = mdx.get(pose.torsoFrameModel);
    if (!frameModel || !torsoModel) return;
    this.skeleton.calc({
      frameModel,
      frame: pose.frame,
      oldFrameModel: mdx.get(pose.oldFrameModel) ?? frameModel,
      oldFrame: pose.oldFrame,
      backlerp: pose.backlerp,
      torsoFrameModel: torsoModel,
      torsoFrame: pose.torsoFrame,
      oldTorsoFrameModel: mdx.get(pose.oldTorsoFrameModel) ?? torsoModel,
      oldTorsoFrame: pose.oldTorsoFrame,
      torsoBacklerp: pose.torsoBacklerp,
      torsoAxis: pose.torsoAxis,
    });

    const body: Orientation = { origin: [s.origin[0], s.origin[1], s.origin[2]], axis: pose.legsAxis };
    this.body.place(body);
    if (this.skinning) this.skinning.setBones(this.skeleton);
    else this.bodySurfaces.forEach((surf, i) => {
      const inst = this.body.surfaces[i];
      this.skeleton.skin(surf, inst.position.array as Float32Array, inst.normal.array as Float32Array);
      inst.position.needsUpdate = true;
      inst.normal.needsUpdate = true;
    });

    // one lighting origin for all parts
    const light = this.kit.light([s.origin[0], s.origin[1], s.origin[2] + 31], s.viewOrigin);
    if (this.skinning) this.skinning.setLight(light, body.axis);
    else this.body.light(light);

    let headOrientation: Orientation | null = null;
    if (this.head) {
      const idle = this.assets.headIdle;
      if (idle) {
        // HD_IDLE1, looping
        const t = (s.time * idle.fps) / 1000;
        const i = Math.floor(t);
        const loop = idle.loopFrames || idle.numFrames;
        const start = idle.numFrames - loop;
        const at = (k: number) => idle.firstFrame + (k < idle.numFrames ? k : start + ((k - start) % loop));
        this.head.setFrame(at(i + 1), at(i), 1 - (t - i));
      }
      const tag = this.bodyTag('tag_head');
      if (tag) {
        headOrientation = placeOnTag(body, tag, pose.headAxis);
        this.head.place(headOrientation);
        this.head.light(light);
      }
      this.head.group.visible = !!tag;
    }
    for (const acc of this.accessories) {
      let tag: Orientation | null;
      let parent: Orientation | null;
      if (acc.onHead) {
        tag = this.head?.tag(acc.tag) ?? null;
        parent = headOrientation;
      } else {
        tag = acc.mdmTag ? this.skeleton.tag(acc.mdmTag) : null;
        parent = body;
      }
      acc.part.group.visible = !!(tag && parent);
      if (!tag || !parent) continue;
      acc.part.place(placeOnTag(parent, tag));
      acc.part.light(light);
    }
    if (this.weapon) {
      const tag = this.bodyTag('tag_weapon');
      this.weapon.group.visible = !!tag;
      if (tag) {
        this.weapon.place(placeOnTag(body, tag));
        this.weapon.light(light);
      }
    }
  }

  dispose(): void {
    this.body.dispose();
    this.skinning?.dispose();
    this.head?.dispose();
    for (const a of this.accessories) a.part.dispose();
    this.weapon?.dispose();
    this.object.removeFromParent();
  }
}

